import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAppContext } from 'services/context';
import { useTranslation } from 'services/hooks';
import { buildStructuredList } from 'services/utils';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import HtmlElement from './HtmlElement';
import HtmlPreview from './HtmlPreview';
import { ROUTES } from 'constants/index';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import './style.css';
import '../Chapter/style.css';

const LOCAL_STORAGE_ELEMENTS_KEY = 'LOCAL_STORAGE_ELEMENTS_KEY';
const LOCAL_STORAGE_NEW_COURSE_KEY = 'LOCAL_STORAGE_NEW_COURSE_KEY';
const LOCAL_STORAGE_NEW_COURSE_ELEMENTS_KEY = 'LOCAL_STORAGE_NEW_COURSE_ELEMENTS_KEY';
const LOCAL_STORAGE_LAST_COURSE = 'LOCAL_STORAGE_LAST_COURSE';

export const ElementType = Object.freeze({
	MAIN_TITLE: 'h3',
	NORMAL_TITLE: 'h4',
	SUB_TITLE: 'h6',
	ARTICLE: 'article',
	PARAGRAPH: 'p',
	UNORDERED_LIST: 'ul',
	ORDERED_LIST: 'ol',
	CUSTOM_QUESTION: 'custom',
	MULTIPLE_CHOICE: 'multiple-choice',
	LINE_BREAK: 'br',
	VIDEO: 'video',
	AUDIO: 'audio',
	IMAGE: 'img',
	TABLE: 'table',
});

//used to format a text across the html editor to replace ** or __ with match tab element
export const formatText = text => {
	if (!text || !text.trim()) return '';

	// Apply bold formatting first
	text = text.replaceAll(/\*\*(.*?)\*\*/g, '<b>$1</b>'); // Bold formatting

	text = text.replaceAll(/\n/g, '<br/>'); // Convert newlines to <br/>

	// Apply underline formatting
	text = text.replaceAll(/`([^`]*)`|--(.*?)--/g, (match, code, underline) => {
		if (code) return code;
		if (underline) return `<u>${underline}</u>`;
		return match;
	});

	text = text.trim(); // Removes whitespace from both ends of string.

	text = text.replaceAll(' ', '&nbsp;'); // Convert spaces to &nbsp;

	return text;
};

export const sanitizeListItem = text => {
	return text.replace(/^[•○▪️➤→▶❖✦\s]+/, '').trim(); // Remove leading bullet-like characters
};

const TRANSLATION_KEY = 'html_editor';

const HtmlEditor = () => {
	const { Api } = useAppContext();

	const [coursesList, setCoursesList] = useState([]);
	const [selectedCourse, setSelectedCourse] = useState(null);
	const [elements, setElements] = useState([]);
	const [newCourseData, setNewCourseData] = useState({});
	const [deletedElements, setDeletedElements] = useState([]); // ✅ Stores deleted elements for undo/redo
	const [insertIndex, setInsertIndex] = useState(null);
	const [type, setType] = useState(ElementType.MAIN_TITLE);
	const [text, setText] = useState('');
	const [selectedHighlightText, setSelectedHighlightText] = useState('');
	const [selectedChapter, setSelectedChapter] = useState(null);
	const [videoSrc, setVideoSrc] = useState(null);
	const [audioSrc, setAudioSrc] = useState(null);
	const [imageSrc, setImageSrc] = useState(null);
	const [articleBody, setArticleBody] = useState('');
	const [listTitle, setListTitle] = useState('');
	const [listItem, setListItem] = useState('');
	const [listItems, setListItems] = useState([]);
	const [nestedIndexes, setNestedIndexes] = useState([]);
	const [isNewCourse, setIsNewCourse] = useState(true);
	const [bold, setBold] = useState(false);
	const [underLine, setUnderLine] = useState(false);
	const [center, setCenter] = useState(false);
	const [link, setLink] = useState(false);
	const [linkUrl, setLinkUrl] = useState('');
	const [questionTitle, setQuestionTitle] = useState('');
	const [answers, setAnswers] = useState([
		{ text: '', isCorrect: false, hint: '' },
		{ text: '', isCorrect: false, hint: '' },
	]);
	const [editingElementId, setEditingElementId] = useState(null);
	const [tableData, setTableData] = useState({ title: '', headers: [], rows: {} });
	const [columnHeader, setColumnHeader] = useState('');
	const [newRow, setNewRow] = useState([]);

	const textareaRef = useRef(null);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const getTranslation = useTranslation();

	const ElementInstructions = {
		[ElementType.MAIN_TITLE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_main_title`),
		[ElementType.NORMAL_TITLE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_normal_title`),
		[ElementType.SUB_TITLE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_sub_title`),
		[ElementType.ARTICLE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_article`),
		[ElementType.PARAGRAPH]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_paragraph`),
		[ElementType.UNORDERED_LIST]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_unordered_list`),
		[ElementType.ORDERED_LIST]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_ordered_list`),
		[ElementType.MULTIPLE_CHOICE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_multiple_choice`),
		[ElementType.LINE_BREAK]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_line_break`),
		[ElementType.VIDEO]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_video`),
		[ElementType.AUDIO]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_audio`),
		[ElementType.IMAGE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_image`),
		[ElementType.TABLE]: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_table`),
	};

	//general instructions section on the controls
	const GENERAL_INSTRUCTIONS = Array.from({ length: 9 }, (_, i) =>
		getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_general_instructions_bullet${i + 1}`),
	);

	useEffect(() => {
		(async () => {
			const courses = await Api.GetAllAvailableCourses();
			if (courses) setCoursesList(courses);
		})();
	}, []);

	const choosedKeyByCourseType = useMemo(() => {
		return isNewCourse ? LOCAL_STORAGE_NEW_COURSE_ELEMENTS_KEY : LOCAL_STORAGE_ELEMENTS_KEY;
	}, [isNewCourse]);

	//handle save data in the localStorage when the user add/edit any element.
	useEffect(() => {
		if (elements.length > 0) {
			const savedData = handleGetLocalStorage();
			if (!savedData || JSON.stringify(savedData) !== JSON.stringify(elements)) {
				// ✅ Only update local storage if the data is actually different
				handleSetLocalStorage(elements);
			}
		}
	}, [elements]);

	//retrive existing new course data onload
	useEffect(() => {
		const newCourseExistingElements = handleGetLocalStorage(LOCAL_STORAGE_NEW_COURSE_ELEMENTS_KEY);
		const newCourseExistingData = handleGetLocalStorage(LOCAL_STORAGE_NEW_COURSE_KEY);
		if (newCourseExistingData) {
			setNewCourseData(newCourseExistingData);
			setSelectedCourse(newCourseExistingData);
			setSelectedChapter(newCourseExistingData?.courseChapters[newCourseExistingData?.courseChapters?.length - 1]);
		}
		if (newCourseExistingElements) setElements(newCourseExistingElements);
	}, []);

	const elementInstructionByType = useMemo(() => {
		return ElementInstructions[type];
	}, [type]);

	// Add global event listener for Ctrl+Z and Ctrl+Y
	useEffect(() => {
		const handleKeyDown = e => {
			if (e.ctrlKey && e.key === 'z') {
				e.preventDefault();
				handleUndo();
			} else if (e.ctrlKey && e.key === 'y') {
				e.preventDefault();
				handleRedo();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [deletedElements, elements]);

	const handleDragEnd = event => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = elements.findIndex(item => item.id === active.id);
			const newIndex = elements.findIndex(item => item.id === over.id);
			setElements(items => arrayMove(items, oldIndex, newIndex));
		}
	};

	const handleTypeChange = e => {
		resetControls();
		// setInsertIndex(null);
		// setEditingElementId(null);
		const selectedType = e.target.value;
		setType(selectedType);
	};

	const addListItem = () => {
		if (!listItem.trim()) return;

		const sanitizedItem = sanitizeListItem(listItem); // Remove bullets from copied text
		setListItems([...listItems, sanitizedItem]);
		setListItem('');
	};

	const removeListItem = index => {
		setListItems(listItems.filter((_, i) => i !== index));

		setNestedIndexes(prevNested =>
			prevNested
				// Remove the deleted index if it was nested.
				.filter(i => i !== index)
				// Shift all indexes that were after it.
				.map(i => (i > index ? i - 1 : i)),
		);
	};

	const toggleNested = index => {
		setNestedIndexes(
			prev =>
				prev.includes(index)
					? prev.filter(i => i !== index) // uncheck
					: [...prev, index], // check
		);
	};

	// Save the word the user highlighted.
	const handleSelectionChange = () => {
		const el = textareaRef.current;
		if (!el) return;

		const { selectionStart, selectionEnd, value } = el;

		// Nothing highlighted.
		if (selectionStart === selectionEnd) {
			setSelectedHighlightText('');
			return;
		}

		const selected = value.substring(selectionStart, selectionEnd);
		setSelectedHighlightText(selected);
	};

	const validateTable = () => {
		const { headers, rows } = tableData;

		// Must have headers.
		if (!headers || !Array.isArray(headers) || !headers.length) {
			return {
				valid: false,
				error: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_table_empty_headers_validation_message`),
			};
		}

		// Must have rows object.
		if (!rows || typeof rows !== 'object' || !Object.keys(rows)?.length) {
			return {
				valid: false,
				error: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_table_empty_rows_validation_message`),
			};
		}

		// Check all header keys exist in rows.
		for (const header of headers) {
			if (!rows.hasOwnProperty(header) || !Array.isArray(rows[header])) {
				return {
					valid: false,
					error: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_table_missing_rows_validation_message`).replace(
						'[header]',
						header,
					),
				};
			}
		}

		// Check all arrays have same length.
		const lengths = headers.map(h => rows[h].length);
		const firstLen = lengths[0];

		for (let i = 0; i < lengths.length; i++) {
			if (lengths[i] !== firstLen) {
				return {
					valid: false,
					error: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_table_row_length_validation_message`),
				};
			}
		}

		// Ensure all cells are filled.
		for (const header of headers) {
			for (let i = 0; i < rows[header].length; i++) {
				if (!rows[header][i].trim()) {
					return {
						valid: false,
						error: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_table_empty_cell_validation_message`)
							.replace('[header]', header)
							.replace('[row]', i + 1),
					};
				}
			}
		}

		return { valid: true };
	};

	//add element to HtmlElement component
	const addElement = () => {
		const genericClassName = `${bold ? 'bold' : ''} ${center ? 'text-center' : ''} ${underLine ? 'underline' : ''}`.trim();
		let newElement = null;
		if ([ElementType.LINE_BREAK].includes(type)) {
			newElement = {
				id: Date.now(),
				type,
				content: '<br/><br/>', // ✅ Always adds two line breaks
			};
		} else if ([ElementType.UNORDERED_LIST, ElementType.ORDERED_LIST].includes(type)) {
			if (listItems.length === 0) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_list_error_message`));
				return;
			}

			newElement = {
				id: Date.now(),
				type,
				listTitle: listTitle.trim(),
				content: listItems,
				nestedIndexes,
				className: genericClassName,
			};
			setListTitle('');
			setListItems([]);
			setNestedIndexes([]);
		} else if ([ElementType.ARTICLE].includes(type)) {
			if (!text.trim() || !articleBody.trim()) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_article_error_message`));
				return;
			}

			newElement = {
				id: Date.now(),
				type,
				title: text,
				body: articleBody,
				className: genericClassName,
			};
			setText('');
			setArticleBody('');
		} else if ([ElementType.MULTIPLE_CHOICE].includes(type)) {
			if (!questionTitle.trim() || answers.some(a => !a.text.trim())) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_multiple_choice_error_message`));
				return;
			}

			// Ensure at least one answer is correct
			const correctAnswers = answers.filter(a => a.isCorrect);
			if (correctAnswers.length === 0) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_answers_error_message`));
				return;
			}

			//// Ensure all incorrect answers have hints
			//const incorrectAnswers = answers.filter(a => !a.isCorrect);
			// if (incorrectAnswers.some(a => !a.hint.trim())) {
			// 	alert('All incorrect answers must have hints.');
			// 	return;
			// }

			newElement = {
				id: Date.now(),
				type,
				questionTitle,
				answers,
				className: genericClassName,
			};
			setQuestionTitle('');
			setAnswers([
				{ text: '', isCorrect: false, hint: '' },
				{ text: '', isCorrect: false, hint: '' },
			]);
		} else if ([ElementType.MAIN_TITLE, ElementType.NORMAL_TITLE, ElementType.SUB_TITLE, ElementType.PARAGRAPH].includes(type)) {
			if (!text.trim()) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_title_error_message`));
				return;
			}

			if (link && !selectedHighlightText.trim()) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_highlight_text_error_message`));
				return;
			}

			if (link && !linkUrl.trim()) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_link_error_message`));
				return;
			}

			if (!link) {
				newElement = {
					id: Date.now(),
					type,
					content: text,
					className: genericClassName,
				};
			} else {
				newElement = {
					id: Date.now(),
					type,
					content: text,
					selectedHighlightText,
					linkUrl,
					className: genericClassName,
				};
			}

			setText('');
			setSelectedHighlightText('');
			setLinkUrl('');
		} else if ([ElementType.TABLE].includes(type)) {
			const isValid = validateTable();

			if (!isValid || !isValid?.valid) {
				alert(isValid?.error);
				return;
			}

			newElement = {
				id: Date.now(),
				type,
				tableData,
				className: genericClassName,
			};

			setTableData({ title: '', headers: [], rows: {} });
			setColumnHeader('');
			setNewRow([]);
		} else if ([ElementType.VIDEO].includes(type)) {
			if (!videoSrc) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_video_error_message`));
				return;
			}

			newElement = {
				id: Date.now(),
				type,
				videoSrc, // ✅ Store the user-defined relative video path
				videoFullSrcPath: `${window.location.origin}/app${ROUTES.IFRAME_VIDEO_PLAYER_PAGE}?video_src=${videoSrc}`,
			};
			setVideoSrc(''); // ✅ Reset input field
		} else if ([ElementType.AUDIO].includes(type)) {
			if (!audioSrc) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_audio_error_message`));
				return;
			}

			newElement = {
				id: Date.now(),
				type,
				audioSrc, // ✅ Store the user-defined relative video path
				audioFullSrcPath: `${window.location.origin}/app${ROUTES.IFRAME_VIDEO_PLAYER_PAGE}?audio_src=${audioSrc}`,
			};
			setAudioSrc(''); // ✅ Reset input field
		} else if ([ElementType.IMAGE].includes(type)) {
			if (!imageSrc.trim()) {
				alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_add_image_error_message`));
				return;
			}

			newElement = {
				id: Date.now(),
				type,
				imageSrc, // ✅ Store the user-defined relative image path
			};
			setImageSrc(''); // ✅ Reset input field
		}

		if (newElement) {
			setElements(prevElements => {
				let updatedElements;

				if (insertIndex !== null) {
					// ✅ Insert at selected position
					updatedElements = [...prevElements.slice(0, insertIndex), newElement, ...prevElements.slice(insertIndex)];
				} else {
					// ✅ Append to the end if no position is selected
					updatedElements = [...prevElements, newElement];
				}

				// handleSetLocalStorage(updatedElements);
				return updatedElements;
			});

			setInsertIndex(null); // ✅ Reset insert position after adding
		}

		resetControls();
	};

	//handles update selected element
	const updateElement = () => {
		const _elements = [...elements];
		const genericClassName = `${bold ? 'bold' : ''} ${center ? 'text-center' : ''} ${underLine ? 'underline' : ''}`.trim();

		// Check table validation.
		if (type === ElementType.TABLE) {
			const isValid = validateTable();

			if (!isValid || !isValid?.valid) {
				alert(isValid?.error);
				return;
			}
		}

		const updatedElements = _elements.map(el => {
			if (el.id !== editingElementId) return el; // ✅ Skip unchanged elements

			// ✅ Handle Different Types Separately
			if ([ElementType.PARAGRAPH, ElementType.MAIN_TITLE, ElementType.NORMAL_TITLE, ElementType.SUB_TITLE].includes(type)) {
				if (link) {
					return {
						...el,
						type,
						content: text,
						selectedHighlightText,
						linkUrl,
						className: genericClassName,
					};
				} else {
					const { selectedHighlightText, linkUrl, ...rest } = el; //Remove these keys from the el object.
					return {
						...rest,
						type,
						content: text,
						className: genericClassName,
					};
				}
			}

			if (type === ElementType.ARTICLE) {
				return {
					...el,
					type,
					title: text,
					body: articleBody,
					className: genericClassName,
				};
			}

			if (type === ElementType.MULTIPLE_CHOICE) {
				return { ...el, type, questionTitle, answers, className: genericClassName };
			}
			if (type === ElementType.TABLE) {
				return { ...el, tableData, className: genericClassName };
			}
			if (type === ElementType.VIDEO) {
				return { ...el, type, videoSrc };
			}
			if (type === ElementType.AUDIO) {
				return { ...el, type, audioSrc };
			}
			if (type === ElementType.IMAGE) {
				return { ...el, type, imageSrc };
			}

			if ([ElementType.UNORDERED_LIST, ElementType.ORDERED_LIST].includes(type)) {
				return {
					...el,
					type,
					listTitle: listTitle.trim(),
					content: listItems,
					nestedIndexes,
					className: genericClassName,
				};
			}

			return el; // ✅ Default case (No changes)
		});

		setElements(updatedElements);
		handleSetLocalStorage(updatedElements);

		// ✅ Reset state after updating
		setInsertIndex(null);
		setEditingElementId(null);
		resetControls();
	};

	//add answer button on MULTIPLE_QUESTION element
	const addAnswer = () => {
		if (answers.length >= 5) return; // Limit answers to 5
		setAnswers([...answers, { text: '', isCorrect: false, hint: '' }]);
	};

	//remove answer button on MULTIPLE_QUESTION element
	const removeAnswer = index => {
		if (answers.length > 2) {
			// Ensure at least 2 answers remain
			setAnswers(answers.filter((_, i) => i !== index));
		}
	};

	const handleEditElement = element => {
		// Reset states first to avoid keeping values from other types
		resetControls();

		setEditingElementId(element?.id);
		setType(element?.type);

		// Now set only relevant states
		if ([ElementType.PARAGRAPH, ElementType.MAIN_TITLE, ElementType.NORMAL_TITLE, ElementType.SUB_TITLE].includes(element.type)) {
			setText(element.content || '');
			setBold(element.className?.includes('bold') || false);
			setCenter(element.className?.includes('text-center') || false);
			setUnderLine(element.className?.includes('underline') || false);

			if (element.selectedHighlightText && element.linkUrl) {
				setLink(true);
				setSelectedHighlightText(element.selectedHighlightText);
				setLinkUrl(element.linkUrl);
			} else {
				setLink(false);
			}
		} else if ([ElementType.UNORDERED_LIST, ElementType.ORDERED_LIST].includes(element.type)) {
			setListTitle(element.listTitle || '');
			setListItems(element.content || []);
			setNestedIndexes(element.nestedIndexes || []);
			setBold(element.className?.includes('bold') || false);
			setCenter(element.className?.includes('text-center') || false);
			setUnderLine(element.className?.includes('underline') || false);
		} else if (element.type === ElementType.ARTICLE) {
			setText(element.title || ''); // ✅ Set title correctly
			setArticleBody(element.body || ''); // ✅ Ensure body is set
			setBold(element.className?.includes('bold') || false);
			setCenter(element.className?.includes('text-center') || false);
			setUnderLine(element.className?.includes('underline') || false);
		} else if (element.type === ElementType.MULTIPLE_CHOICE) {
			setQuestionTitle(element.questionTitle || ''); // ✅ Fix: Set question title properly
			setCenter(element.className?.includes('text-center') || false);
			setUnderLine(element.className?.includes('underline') || false);
			setAnswers(
				element.answers || [
					{ text: '', isCorrect: false, hint: '' },
					{ text: '', isCorrect: false, hint: '' },
				],
			);
		} else if (element.type === ElementType.TABLE) {
			setBold(element.className?.includes('bold') || false);
			setCenter(element.className?.includes('text-center') || false);
			setUnderLine(element.className?.includes('underline') || false);
			setTableData(element.tableData);
		} else if (element.type === ElementType.VIDEO) {
			setVideoSrc(element.videoSrc);
		} else if (element.type === ElementType.AUDIO) {
			setAudioSrc(element.audioSrc);
		} else if (element.type === ElementType.IMAGE) {
			setImageSrc(element.imageSrc);
		}
	};

	//reset all the controls state to prepre it for the next element
	const resetControls = () => {
		setText('');
		setListTitle('');
		setListItem('');
		setListItems([]);
		setNestedIndexes([]);
		setBold(false);
		setCenter(false);
		setUnderLine(false);
		setLink(false);
		setLinkUrl('');
		setSelectedHighlightText('');
		setTableData({ title: '', headers: [], rows: {} });
		setQuestionTitle('');
		setArticleBody('');
		setVideoSrc(null);
		setAudioSrc(null);
		setAnswers([
			{ text: '', isCorrect: false, hint: '' },
			{ text: '', isCorrect: false, hint: '' },
		]);
		setType(ElementType.MAIN_TITLE);
	};

	// Handle Ctrl+Z (Undo) - Restore last deleted element
	const handleUndo = () => {
		if (deletedElements.length === 0) return;

		setDeletedElements(prevDeleted => {
			const lastDeleted = prevDeleted[prevDeleted.length - 1];
			setElements(prevElements => [...prevElements, lastDeleted]);

			return prevDeleted.slice(0, -1); // Remove last deleted
		});
	};
	const handleRedo = () => {
		if (elements.length === 0 || deletedElements.length > 0) return;

		setElements(prevElements => {
			const lastRestored = prevElements[prevElements.length - 1];

			// Move it back to deleted stack
			setDeletedElements(prev => [...prev, lastRestored]);

			return prevElements.slice(0, -1); // Remove last element from elements
		});
	};

	//remove specific element from the htmlElement component
	const removeElement = id => {
		if (window.confirm(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_delete_message`))) {
			setElements(prevElements => {
				const updatedElements = prevElements.filter(el => el.id !== id);
				const deletedItem = prevElements.find(el => el.id === id);

				if (deletedItem) {
					setDeletedElements(prevDeleted => [deletedItem, ...prevDeleted]); // ✅ Store deleted elements for undo
				}

				handleSetLocalStorage(updatedElements);
				return updatedElements;
			});
		}
	};

	const handleSave = () => {
		// Retrieve existing elements from local storage
		const existingData = handleGetLocalStorage();
		const previousElements = existingData ? existingData : [];

		// ✅ Remove duplicates by checking unique IDs
		const updatedElements = [...previousElements];

		elements.forEach(newEl => {
			const exists = previousElements.some(prevEl => prevEl.id === newEl.id);
			if (!exists) {
				updatedElements.push(newEl); // ✅ Only add if not already in local storage
			}
		});

		// ✅ Save updated elements and reset TTL
		handleSetLocalStorage(updatedElements);
		alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_save_message`));
	};

	//textarea article
	const handleArticleBodyChange = e => {
		let rawText = e.target.value;

		// Store only raw text (no <br> elements)
		setArticleBody(rawText);
	};

	//export all the elements as html file
	const handleExportHTML = () => {
		// 👇 This is your actual full HTML content
		const htmlString = elements
			.map(el => {
				const classAttr = el?.className ? ` class="${el.className}"` : '';
				const headingElements = new Set([ElementType.MAIN_TITLE, ElementType.NORMAL_TITLE, ElementType.SUB_TITLE]);

				if (headingElements.has(el.type)) {
					return `<${el.type}${classAttr}>${formatText(el.content)}</${el.type}>`;
				}

				if ([ElementType.ORDERED_LIST, ElementType.UNORDERED_LIST].includes(el.type)) {
					const structuredList = buildStructuredList(el.content, el.nestedIndexes);
					const listTitle = el.listTitle ? `<div class="list-title ${el?.className}">${el.listTitle}</div>\n` : '';
					const listItems = structuredList
						?.map(item => {
							const childrenHtml =
								item?.children?.length > 0
									? `<${el.type}>
							${item.children.map(child => `\t<li>${formatText(child)}</li>`).join('\n')}
							</${el.type}>`
									: '';

							return `<li>
\t<span>${formatText(item.parent)}</span>
\t${childrenHtml}
</li>`;
						})
						.join('\n');

					return `<div>\n${listTitle}<${el.type}>\n${listItems}\n</${el.type}>\n</div>`;
				}

				if (el.type === ElementType.LINE_BREAK) {
					return `<br/><br/>`;
				}

				if (el.type === ElementType.PARAGRAPH) {
					let _formattedText = formatText(el.content);

					if (el.selectedHighlightText && el.linkUrl) {
						const _link = `<a href="${el.linkUrl}" target="_blank" rel="noopener noreferrer">${el.selectedHighlightText}</a>`;
						_formattedText = _formattedText?.replace(formatText(el.selectedHighlightText), _link);
					}
					return `<p${classAttr}>${_formattedText}</p>`;
				}

				if (el.type === ElementType.ARTICLE) {
					return `
<div class="article">
<h6 class="${el.className}">${formatText(el.title)}</h6>
<p>${el.body.replace(/\n/g, '<br><br>')}</p>
</div>`;
				}

				if (el.type === ElementType.MULTIPLE_CHOICE) {
					const choices = el.answers
						.map(
							(answer, i) => `
<div class="multiple-choice-option">
  <input type="checkbox" id="choice-${el.id}-${i}" name="question-${el.id}"
    class="${answer.isCorrect ? 'correct-answer' : 'wrong-answer'}"
    onclick="document.querySelectorAll('[name=question-${el.id}]').forEach(cb => { if (cb !== this) cb.checked = false; })">
  <label for="choice-${el.id}-${i}">${answer.text}</label>
  ${!answer.isCorrect ? `<p class="hint">${answer.hint}</p>` : ''}
</div>`,
						)
						.join('');
					return `
<div class="multiple-choice">
<h6>${el.questionTitle}</h6>
${choices}
</div>`;
				}

				if (el.type === ElementType.TABLE) {
					return `
<table>
  ${tableData?.title ? `<caption ${classAttr}>${tableData.title}</caption>` : ''}
  <thead>
    <tr>
      ${tableData?.headers?.map(header => `<th>${header}</th>`).join('')}
    </tr>
  </thead>
  <tbody>
    ${Array.from({ length: tableData?.rows[tableData.headers[0]]?.length })
			.map((_, rowIdx) => {
				return `
            <tr>
              ${tableData.headers.map(header => `<td>${tableData.rows[header][rowIdx] || ''}</td>`).join('')}
            </tr>
          `;
			})
			.join('')}
  </tbody>
</table>
`;
				}

				if (el.type === ElementType.VIDEO) {
					return `
<div class="video">
  <iframe
    src="${el.videoFullSrcPath}" 
    width="100%"
    height="100%"
    allowfullscreen
  ></iframe>
</div>`;
				}

				if (el.type === ElementType.AUDIO) {
					return `
<div class="audio">
  <iframe
    src="${el.audioFullSrcPath}" 
    width="100%"
    height="100%"
  ></iframe>
</div>`;
				}

				if (el.type === ElementType.IMAGE) {
					return `
<img src="${el.imageSrc}" alt="image-${el.id}" />`;
				}

				return `<div${classAttr}>${formatText(el.content)}</div>`;
			})
			.join('\n');

		const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_exported_html_title`)}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    iframe { border: none; }
    .audio, .video { height: 300px; margin-bottom: 20px; }
    .multiple-choice-option { margin: 10px 0; }
    .hint { font-size: 0.9em; color: gray; }
  </style>
</head>
<body>
  ${htmlString}
</body>
</html>`;

		const blob = new Blob([htmlContent], { type: 'text/html' });
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = 'exported_page.html';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleElementOnClick = (index, elementId) => {
		//if we click on different element its reset the current edited id
		if (editingElementId === elementId) return;
		else {
			resetControls();
			setEditingElementId(null);
			setInsertIndex(prev => (prev === index ? null : index));
		}
	};

	//sets local storage data
	const handleSetLocalStorage = (jsonString, key) => {
		if (key) localStorage.setItem(key, JSON.stringify(jsonString));
		else {
			localStorage.setItem(choosedKeyByCourseType, JSON.stringify(jsonString));
		}
	};
	//returns local storage data by key
	const handleGetLocalStorage = key => {
		if (key) return JSON.parse(localStorage.getItem(key));
		return JSON.parse(localStorage.getItem(choosedKeyByCourseType));
	};
	//reset all saved data button
	const handleResetLocalStorage = () => {
		if (window.confirm(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_saved_data_note`))) {
			handleRemoveItem(LOCAL_STORAGE_ELEMENTS_KEY);
			handleRemoveItem(LOCAL_STORAGE_NEW_COURSE_ELEMENTS_KEY);
			handleRemoveItem(LOCAL_STORAGE_LAST_COURSE);
			handleRemoveItem(LOCAL_STORAGE_NEW_COURSE_KEY);
			resetControls();
			setNewCourseData({});
			setElements([]);
			alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_saved_data_success_message`));
		}
	};
	//reset all saved data button
	const handleDeleteCurrentChapter = () => {
		if (window.confirm(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_delete_chapter_message`))) {
			let courseLayout = handleGetLocalStorage(LOCAL_STORAGE_NEW_COURSE_KEY);
			let chapterToDelete = courseLayout.courseChapters.find(chap => chap.chapterId === selectedChapter.chapterId);
			courseLayout.courseChapters = courseLayout?.courseChapters?.filter(chap => chap.chapterId !== chapterToDelete.chapterId);
			handleSetLocalStorage(courseLayout, LOCAL_STORAGE_NEW_COURSE_KEY);
			setNewCourseData(courseLayout);
			let lastChapter = courseLayout?.courseChapters[courseLayout?.courseChapters?.length - 1];
			setSelectedChapter(lastChapter);
			setElements(lastChapter.body);
		}
	};
	//remove item from the local storage by key
	const handleRemoveItem = key => {
		localStorage.removeItem(key);
	};

	//renders element by type
	const renderElementsByType = useMemo(() => {
		switch (type) {
			case ElementType.ARTICLE:
				return (
					<>
						<input
							type="text"
							className="article-title-input"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_article_field_title`)}
							value={text}
							onChange={e => setText(e.target.value)}
						/>

						<textarea
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_article_field_body`)}
							value={articleBody.replace(/<br>/g, '\n')}
							rows={6}
							onChange={handleArticleBodyChange}
						/>

						{/* Toolbar for Article */}
						<div className="toolbar">
							<button className={center ? 'center-active' : ''} onClick={() => setCenter(!center)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_center_title`)}
							</button>
							{/* <button className={underLine ? 'underline-active' : ''} onClick={() => setUnderLine(!underLine)}>
								Undeline Title
							</button> */}
						</div>
					</>
				);

			case ElementType.PARAGRAPH:
			case ElementType.MAIN_TITLE:
			case ElementType.NORMAL_TITLE:
			case ElementType.SUB_TITLE:
				return (
					<>
						<textarea
							type="text"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_title_field`)}
							value={text}
							rows={6}
							ref={textareaRef}
							onSelect={handleSelectionChange}
							onKeyUp={handleSelectionChange}
							onMouseUp={handleSelectionChange}
							onChange={({ target }) => setText(target?.value)}
						/>
						{link && type === ElementType.PARAGRAPH && (
							<>
								{selectedHighlightText && (
									<div>
										<span className="bold">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_highlight_text_field`)} </span>
										{selectedHighlightText}
									</div>
								)}
								<input
									type="text"
									placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_link_field`)}
									value={linkUrl}
									onChange={({ target }) => setLinkUrl(target?.value)}
								/>
							</>
						)}
						{/* Toolbar for Text Elements */}
						{type !== ElementType.MAIN_TITLE && (
							<div className="toolbar">
								{type === ElementType.PARAGRAPH && (
									<>
										<button className={link ? 'link-active' : ''} onClick={() => setLink(!link)}>
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_link`)}
										</button>

										<button className={bold ? 'bold-active' : ''} onClick={() => setBold(!bold)}>
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_bold`)}
										</button>
									</>
								)}
								<button className={underLine ? 'underline-active' : ''} onClick={() => setUnderLine(!underLine)}>
									{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_underline`)}
								</button>
								<button className={center ? 'center-active' : ''} onClick={() => setCenter(!center)}>
									{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_center`)}
								</button>
							</div>
						)}
					</>
				);

			case ElementType.UNORDERED_LIST:
			case ElementType.ORDERED_LIST:
				return (
					<>
						<input
							type="text"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_list_field`)}
							value={listTitle}
							onChange={e => setListTitle(e.target.value)}
						/>

						<div className="toolbar">
							<button className={bold ? 'bold-active' : ''} onClick={() => setBold(!bold)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_bold_title`)}
							</button>
							<button className={underLine ? 'underline-active' : ''} onClick={() => setUnderLine(!underLine)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_underline_title`)}
							</button>
							<button className={center ? 'center-active' : ''} onClick={() => setCenter(!center)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_center_title`)}
							</button>
						</div>

						<input
							type="text"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_list_item_field`)}
							value={listItem}
							onChange={e => setListItem(e.target.value)}
						/>
						<button onClick={addListItem}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_list_item_button`)}</button>

						{React.createElement(
							type === ElementType.ORDERED_LIST ? 'ol' : 'ul',
							null,
							listItems.map((item, index) => (
								<div key={index} className="list-item-container">
									<input
										type="text"
										value={item}
										onChange={({ target }) => {
											const _updatedList = listItems.map((currentItem, idx) =>
												idx === index ? sanitizeListItem(target?.value) : currentItem,
											);
											setListItems(_updatedList);
										}}
									/>
									<input type="checkbox" checked={nestedIndexes.includes(index)} onChange={() => toggleNested(index)} />
									<button className="remove-item-btn" onClick={() => removeListItem(index)}>
										X
									</button>
								</div>
							)),
						)}
					</>
				);

			case ElementType.MULTIPLE_CHOICE:
				return (
					<>
						<input
							type="text"
							className="question-title"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_multiple_choice_field`)}
							value={questionTitle}
							onChange={e => setQuestionTitle(e.target.value)}
						/>

						<div className="toolbar">
							<button className={underLine ? 'underline-active' : ''} onClick={() => setUnderLine(!underLine)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_underline_title`)}
							</button>
							<button className={center ? 'center-active' : ''} onClick={() => setCenter(!center)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_center_title`)}
							</button>
						</div>

						<div className="multiple-choice-container">
							{answers.map((answer, index) => (
								<div key={index} className="answer-row">
									<input
										type="text"
										className="answer-input"
										placeholder={`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_answer_field`)} ${index + 1}`}
										value={answer.text}
										onChange={e => {
											const updated = [...answers];
											updated[index].text = e.target.value;
											setAnswers(updated);
										}}
									/>

									<label className="correct-checkbox">
										<input
											type="checkbox"
											checked={answer.isCorrect}
											onChange={() => {
												setAnswers(answers.map((a, i) => ({ ...a, isCorrect: i === index })));
											}}
										/>
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_correct_answer_checkbox_field`)}
									</label>

									{!answer.isCorrect && (
										<input
											type="text"
											className="hint-input"
											placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_hint_field`)}
											value={answer.hint}
											onChange={e => {
												const updated = [...answers];
												updated[index].hint = e.target.value;
												setAnswers(updated);
											}}
										/>
									)}

									{answers.length > 2 && (
										<button className="remove-answer-btn" onClick={() => removeAnswer(index)}>
											✖
										</button>
									)}
								</div>
							))}
						</div>

						{answers.length < 5 && (
							<button className="add-answer-btn" onClick={addAnswer}>
								+ {getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_answer_button`)}
							</button>
						)}
					</>
				);

			case ElementType.TABLE:
				return (
					<>
						<input
							type="text"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_table_field_title`)}
							value={tableData?.title || ''}
							onChange={({ target }) => setTableData(prev => ({ ...prev, title: target?.value }))}
						/>

						<div className="toolbar">
							<button className={bold ? 'bold-active' : ''} onClick={() => setBold(!bold)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_bold_title`)}
							</button>
							<button className={underLine ? 'underline-active' : ''} onClick={() => setUnderLine(!underLine)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_underline_title`)}
							</button>
							<button className={center ? 'center-active' : ''} onClick={() => setCenter(!center)}>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_toolbar_center_title`)}
							</button>
						</div>

						<input
							type="text"
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_table_field_header`)}
							value={columnHeader || ''}
							onChange={({ target }) => setColumnHeader(target?.value)}
						/>
						<button
							onClick={() => {
								let _updatedHeaders = [...tableData?.headers];
								_updatedHeaders.push(columnHeader);
								setColumnHeader('');
								setTableData({ ...tableData, headers: _updatedHeaders });
							}}
						>
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_table_header_button`)}
						</button>

						{React.createElement(
							ElementType.UNORDERED_LIST,
							null,
							tableData?.headers?.map((header, index) => (
								<div key={index} className="list-item-container header_data">
									<span dangerouslySetInnerHTML={{ __html: formatText(header) }}></span>
									<button
										className="remove-item-btn"
										onClick={() => {
											let _updatedHeaders = tableData?.headers?.filter((_, i) => i !== index);
											setTableData(prev => ({ ...prev, headers: _updatedHeaders }));
										}}
									>
										X
									</button>
									<div className="header_rows_data">
										{React.createElement(
											ElementType.UNORDERED_LIST,
											null,
											tableData?.rows &&
												tableData?.rows[header] &&
												tableData?.rows[header].map((row, idx) => (
													<div key={idx} className="list-item-container row_data">
														<span dangerouslySetInnerHTML={{ __html: formatText(row) }}></span>
														<button
															className="remove-item-btn"
															onClick={() => {
																let _updatedRows = { ...tableData?.rows };
																_updatedRows[header] = _updatedRows[header].filter((_, i) => i !== idx);
																setTableData(prev => ({ ...prev, rows: _updatedRows }));
															}}
														>
															X
														</button>
													</div>
												)),
										)}
									</div>
								</div>
							)),
						)}

						{tableData?.headers &&
							!!tableData?.headers?.length &&
							tableData?.headers?.map((header, index) => (
								<div className="rows_container">
									<input
										type="text"
										placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_table_field_row`).replace('[header]', header)}
										value={newRow[index] || ''}
										onChange={({ target }) => {
											let _updatedNewRow = [...newRow];
											_updatedNewRow[index] = target?.value;
											setNewRow(_updatedNewRow);
										}}
									/>
									<button
										onClick={() => {
											let _updatedRows = { ...tableData?.rows };
											if (!_updatedRows[header]) _updatedRows[header] = [];
											_updatedRows[header].push(newRow[index]);
											setTableData({ ...tableData, rows: _updatedRows });

											let _resetNewRow = [...newRow];
											_resetNewRow[index] = '';
											setNewRow(_resetNewRow);
										}}
									>
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_table_row_button`).replace('[header]', header)}
									</button>
								</div>
							))}
					</>
				);

			case ElementType.VIDEO:
				return (
					<input
						type="text"
						placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_video_field`)}
						defaultValue={videoSrc}
						onChange={e => setVideoSrc(e.target.value)}
					/>
				);
			case ElementType.AUDIO:
				return (
					<input
						type="text"
						placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_audio_field`)}
						defaultValue={audioSrc}
						onChange={e => setAudioSrc(e.target.value)}
					/>
				);
			case ElementType.IMAGE:
				return (
					<input
						type="text"
						placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_image_field`)}
						defaultValue={imageSrc}
						onChange={e => setImageSrc(e.target.value)}
					/>
				);

			default:
				return null;
		}
	}, [
		type,
		text,
		selectedHighlightText,
		articleBody,
		listTitle,
		listItem,
		listItems,
		nestedIndexes,
		questionTitle,
		answers,
		bold,
		center,
		underLine,
		link,
		linkUrl,
		tableData,
		columnHeader,
		newRow,
		videoSrc,
		audioSrc,
		imageSrc,
		getTranslation,
	]);

	//transform existing course chapters to elements state struct
	const parseElements = chapter => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(chapter.chapterBodyText, 'text/html');
		return Array.from(doc.body.children).map(el => {
			let content = el.innerHTML;
			let imageUrl;
			let videoUrl;
			let audioUrl;

			if (el.tagName.toLowerCase() === ElementType.VIDEO) {
				videoUrl = el.src.trim();
			}
			if (el.tagName.toLowerCase() === ElementType.AUDIO) {
				audioUrl = el.src.trim();
			}
			if (el.tagName.toLowerCase() === ElementType.IMAGE) {
				imageUrl = el.src.trim();
			}
			if (el.tagName.toLowerCase() === 'ul' || el.tagName.toLowerCase() === 'ol') {
				content = Array.from(el.children).map(li => li.innerHTML);
			}

			return {
				id: Date.now() + Math.random(),
				type: el.tagName.toLowerCase(),
				content: content,
				videoFullSrcPath: videoUrl || null,
				audioFullSrcPath: audioUrl || null,
				imageSrc: imageUrl || null,
				className: el.className || '',
			};
		});
	};

	//loads the specific clicked chapter
	const handleLoadChapter = chapter => {
		setIsNewCourse(false);

		handleSetLocalStorage(
			{
				selectedCourse,
				chapter,
			},
			LOCAL_STORAGE_LAST_COURSE,
		);
		let elementsArray;
		if (!chapter || !chapter.chapterBodyText) {
			alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_load_chapter_error_message`));
			return;
		} else {
			setSelectedChapter(chapter);
			elementsArray = parseElements(chapter);
			// Convert HTML string to elements for the editor
		}

		setElements(elementsArray);
		handleSetLocalStorage(elementsArray, LOCAL_STORAGE_ELEMENTS_KEY); // ✅ Save last edited chapter
	};

	const handleLoadNewChapter = newChapter => {
		if (newChapter && newChapter.body) {
			setSelectedChapter(newChapter);
			setElements(newChapter.body);
		}
	};

	//prepare the dom for New Course New/exisiting chapters
	const handleNewCourseClick = () => {
		setIsNewCourse(true);
		resetControls();
		setElements([]);
		setSelectedChapter(null);
		setSelectedCourse(null);
	};

	//save new course new chapter and prepare for the new chapter insertion
	const handleSaveNewChapter = () => {
		const chapterData = handleGetLocalStorage();
		if (chapterData) {
			let courseLayout = handleGetLocalStorage(LOCAL_STORAGE_NEW_COURSE_KEY);
			if (!courseLayout) {
				courseLayout = {
					courseId: 'new-course',
					courseName: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_course_name`),
					courseChapters: [],
				};
			}

			// Create the new chapter object
			const newChapter = {
				chapterId: Date.now(),
				chapterTitle: `${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_chapter_name`)} ${
					courseLayout.courseChapters.length + 1 || 1
				}`,
				body: chapterData,
			};

			// Update the chapters array with the new chapter
			const updatedCourse = {
				...courseLayout,
				courseChapters:
					selectedChapter && courseLayout.courseChapters.some(chap => chap.chapterId === selectedChapter.chapterId)
						? courseLayout.courseChapters.map(chap =>
								chap.chapterId === selectedChapter.chapterId ? { ...chap, body: chapterData } : chap,
							)
						: [...courseLayout.courseChapters, newChapter],
			};

			// Save the updated course data back to local storage
			handleSetLocalStorage(updatedCourse, LOCAL_STORAGE_NEW_COURSE_KEY);
			setNewCourseData(updatedCourse);

			// Prepare the editor for a new chapter
			setElements([]);
			resetControls(); // Reset input fields and states

			alert(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_chapter_success_message`));
		} else {
			console.warn('No chapter data found or course layout is empty.');
		}
	};

	return (
		<div className="html-editor">
			<div className="html-editor__menu">
				<CoursesMenu
					isNewCourse={isNewCourse}
					setIsNewCourse={setIsNewCourse}
					coursesList={coursesList}
					onChapterClick={handleLoadChapter}
					onNewCourseClick={handleNewCourseClick}
					onNewChapterClick={handleLoadNewChapter}
					selectedCourse={selectedCourse}
					setSelectedCourse={setSelectedCourse}
					newCourse={newCourseData}
				/>
			</div>
			<div className="html-editor__container">
				<div className="controls">
					<div className="controls__title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_title`)}</div>
					<select className="element-options-select" value={type} onChange={handleTypeChange}>
						<option value={ElementType.MAIN_TITLE}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_main_title`)}</option>
						<option value={ElementType.NORMAL_TITLE}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_normal_title`)}</option>
						<option value={ElementType.SUB_TITLE}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_sub_title`)}</option>
						<option value={ElementType.LINE_BREAK}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_line_break`)}</option>
						<option value={ElementType.PARAGRAPH}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_paragraph`)}</option>
						<option value={ElementType.ARTICLE}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_article`)}</option>
						<option value={ElementType.UNORDERED_LIST}>
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_unordered_list`)}
						</option>
						<option value={ElementType.ORDERED_LIST}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_ordered_list`)}</option>
						<option value={ElementType.MULTIPLE_CHOICE}>
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_multiple_choice`)}
						</option>
						<option value={ElementType.VIDEO}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_video`)}</option>
						<option value={ElementType.AUDIO}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_audio`)}</option>
						<option value={ElementType.IMAGE}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_image`)}</option>
						<option value={ElementType.TABLE}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_table`)}</option>
					</select>
					{renderElementsByType}
					<button onClick={editingElementId ? updateElement : addElement}>
						{editingElementId
							? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_update_button`)
							: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_controls_add_button`)}
					</button>

					<div className="controls__instructions">
						<div className="controls__instructions__byType">
							<div className="controls__instructions__byType__title">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_element_instructions_title`)}
							</div>
							{elementInstructionByType || null}
						</div>
						<div className="controls__instructions__general">
							<div className="controls__instructions__general__title">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_general_instructions_title`)}
							</div>
							<ul>
								{GENERAL_INSTRUCTIONS.map((instruction, index) => (
									<li key={index} dangerouslySetInnerHTML={{ __html: formatText(instruction) }}></li>
								))}
							</ul>
						</div>
					</div>
					<div className="btn-container">
						{isNewCourse && (
							<button onClick={handleSaveNewChapter} className="save-btn">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_save_button`)}
							</button>
						)}
						{/* <button onClick={handleSave} className="save-btn">
							Save current state
						</button> */}
						<button onClick={handleExportHTML} className="export-btn">
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_export_button`)}
						</button>
						<button onClick={handleDeleteCurrentChapter} className="reset-btn">
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_delete_button`)}
						</button>
						<button onClick={handleResetLocalStorage} className="reset-btn">
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_button`)}
						</button>
					</div>
				</div>

				<div className="structured-html Chapter">
					{elements && (
						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
							<SortableContext items={elements} strategy={verticalListSortingStrategy}>
								{elements?.map((el, index) => (
									<HtmlElement
										key={`${el.id}-${index}`}
										data={el}
										onRemove={removeElement}
										setElements={setElements}
										elements={elements}
										index={index}
										onClickElement={handleElementOnClick}
										insertIndex={insertIndex}
										editingElementId={editingElementId}
										handleEditElement={handleEditElement}
									/>
								))}
							</SortableContext>
						</DndContext>
					)}
				</div>
				<HtmlPreview elements={elements} />
			</div>
		</div>
	);
};

export default HtmlEditor;

const CoursesMenu = ({
	coursesList,
	onChapterClick,
	onNewCourseClick,
	selectedCourse,
	setSelectedCourse,
	onNewChapterClick,
	isNewCourse,
	newCourse,
	setIsNewCourse,
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const [animationCompleted, setAnimationCompleted] = useState(false); // Track animation completion

	const getTranslation = useTranslation();

	const menuClassName = classNames('CoursesMenu', {
		open: isHovered && animationCompleted,
		closed: !isHovered && animationCompleted,
	});

	const handleOnClickCourseItem = course => {
		setIsNewCourse(false);
		setSelectedCourse(prev => (prev?.courseId === course.courseId ? null : course));
	};

	const handleOnClickNewCourse = course => {
		setSelectedCourse(newCourse);
		onNewCourseClick && typeof onNewCourseClick === 'function' && onNewCourseClick(course);
	};

	const handleOnClickChapter = chapter => {
		if (isNewCourse) {
			onNewChapterClick && typeof onNewChapterClick === 'function' && onNewChapterClick(chapter);
		} else {
			onChapterClick && typeof onChapterClick === 'function' && onChapterClick(chapter);
		}
	};

	return (
		<motion.div
			initial={{ width: 45 }}
			animate={{ width: isHovered ? 300 : 45 }}
			transition={{ duration: 0.3 }}
			onMouseEnter={() => {
				setIsHovered(true);
			}}
			onMouseLeave={() => {
				setIsHovered(false);
				setAnimationCompleted(false);
			}}
			onAnimationComplete={() => {
				if (isHovered) setAnimationCompleted(true);
			}}
			className={menuClassName}
		>
			<div className="CoursesMenu__title">
				{isHovered && animationCompleted
					? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_menu_title`)
					: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_menu_hovered_title`)}
			</div>
			<div className="CoursesMenu__course_list">
				<CourseItem
					key="new-course"
					isNew={isNewCourse}
					course={{
						courseId: 'new-course',
						courseName: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_course_name`),
						courseChapters: newCourse?.chapters || [],
					}}
					onClick={handleOnClickNewCourse}
					onClickChapter={handleOnClickChapter}
					newCourseExistingData={newCourse}
					isHovered={isHovered}
					animationCompleted={animationCompleted}
				/>

				{coursesList?.map((course, index) => (
					<CourseItem
						key={course.courseId}
						index={index}
						course={course}
						selectedCourse={selectedCourse}
						onClickChapter={handleOnClickChapter}
						onClick={handleOnClickCourseItem}
						isHovered={isHovered}
						animationCompleted={animationCompleted}
					/>
				))}
			</div>
		</motion.div>
	);
};

const CourseItem = ({
	course,
	onClick,
	onClickChapter,
	selectedCourse,
	isHovered,
	animationCompleted,
	newCourseExistingData,
	isNew = false,
}) => {
	const getTranslation = useTranslation();

	const subStringLongStrings = string => {
		return string.length > 28 ? string.substring(0, 28) + '...' : string;
	};

	const handleOnClick = () => {
		onClick && typeof onClick === 'function' && onClick(course, isNew);
	};

	const handleCourseChapterOnClick = (chapter, e) => {
		e.stopPropagation();
		onClickChapter && typeof onClickChapter === 'function' && onClickChapter(chapter);
	};
	const loadNewChapterData = newChapter => {
		newChapter && onClickChapter && typeof onClickChapter === 'function' && onClickChapter(newChapter);
	};

	const getColor = useMemo(() => {
		const courseColors = [
			'F77F00',
			'EAE2B7',
			'D62828',
			'7cb518',
			'3d348b',
			'2176ff',
			'a1c181',
			'FCBF49',
			'e76f51',
			'5c8001',
			'43aa8b',
			'5a189a',
			'EAE2B7',
			'a4243b',
			'9dd9d2',
			'ff4800',
			'33a1fd',
			'bd632f',
			'babd8d',
			'5a189a',
			'ff5400',
			'f94144',
			'219ebc',
		];

		return `#${courseColors[course.courseId] || courseColors[0]}`; // Always return the same color for the same courseId
	}, [course]);

	return (
		<div className={`CourseItem`} key={course.courseId}>
			<div className="CourseItem__course-header" onClick={handleOnClick}>
				<div className="CourseItem__course-header__color_bullet" style={{ backgroundColor: getColor }}></div>
				<AnimatePresence>
					{animationCompleted && isHovered && (
						<motion.div
							initial={{ opacity: 0, x: -10 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -10 }}
							transition={{ duration: 0.3 }}
							className="CourseItem__course-header__text"
							title={course.courseName}
						>
							{isNew
								? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_course_name`)
								: course.courseId !== 'new-course'
									? `${course?.courseId} - ${subStringLongStrings(course.courseName)}`
									: course.courseName}
						</motion.div>
					)}
				</AnimatePresence>
			</div>
			{animationCompleted && isHovered && (selectedCourse?.courseId === course.courseId || isNew) && (
				<AnimatePresence>
					<motion.div
						className="CourseItem__chapter-list"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
					>
						{course?.units &&
							course?.units?.map(unit =>
								unit.courseChapters?.map(chapter => (
									<div>
										<div key={chapter?.courseChapterId} className={`chapter-item`} onClick={e => handleCourseChapterOnClick(chapter, e)}>
											{unit?.title} - {chapter?.chapterName}
										</div>
									</div>
								)),
							)}
						{course?.courseChapters?.map(chapter => (
							<div>
								<div key={chapter?.courseChapterId} className={`chapter-item`} onClick={e => handleCourseChapterOnClick(chapter, e)}>
									{chapter?.chapterName}
								</div>
							</div>
						))}
						{isNew &&
							newCourseExistingData &&
							newCourseExistingData.courseChapters?.map(newChapter => {
								return (
									<div>
										<div key={newChapter.chapterId} className={`chapter-item`} onClick={() => loadNewChapterData(newChapter)}>
											{newChapter ? newChapter.chapterTitle : null}
										</div>
									</div>
								);
							})}
						{!isNew && !course?.courseChapters.length && (
							<div className="chapter-item">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_no_avaliable_message`)}</div>
						)}
					</motion.div>
				</AnimatePresence>
			)}
		</div>
	);
};
